import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Upload, ExternalLink, FileDown } from "lucide-react";
import api from "@/lib/api";
import { downloadBlob, errMsg } from "@/lib/customerApi";

const STATUSES = ["Received", "In Progress", "Ready", "Paid in Full", "Delivered"];
const KINDS = [["full_report", "Full report"], ["executive_summary", "One-page summary"], ["comparison_table", "Comparison table"], ["other", "Additional file"]];
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx";
const TEMPLATES = [["full_report", "Full Report"], ["executive_summary", "Executive Summary"], ["comparison_table", "Provider Comparison"]];

const PrefilledTemplates = ({ o }) => (
  <div className="mt-4">
    <p className="text-[11px] text-slate-500 mb-2">Start the report: fillable PDFs pre-filled with this order's site, acreage, target MW, customer, and known utilities.</p>
    <div className="flex flex-wrap gap-2">
      {TEMPLATES.map(([slot, label]) => (
        <button key={slot} data-testid={`prefill-${slot}-${o.order_number}`}
          onClick={() => downloadBlob(api, `/admin/power-studies/${o.id}/template/${slot}`, `${o.order_number}-${label.replace(/ /g, "-")}.pdf`).catch(() => toast.error("Download failed"))}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-[#F59E0B] text-[#0B1F3A] font-semibold hover:bg-[#fbbf24]">
          <FileDown size={13} /> {label}
        </button>
      ))}
    </div>
  </div>
);

const Info = ({ o }) => (
  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
    {[["Contact", `${o.name}${o.company ? ` · ${o.company}` : ""}`], ["Email", o.email], ["Phone", o.phone],
      ["Site", o.site_address || "—"], ["Parcel ID", o.parcel_id || "—"], ["Acreage / MW", `${o.acreage} ac · ${o.target_mw} MW`],
      ["Utilities", o.utilities || "—"], ["Notes", o.notes || "—"]].map(([k, v]) => (
      <p key={k}><span className="text-slate-500">{k}: </span><span className="text-slate-200 break-words">{v}</span></p>
    ))}
    {o.brochure_url && <a href={o.brochure_url} target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] inline-flex items-center gap-1">Listing link <ExternalLink size={12} /></a>}
  </div>
);

const UploadBtn = ({ o, kind, label, reload }) => {
  const up = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    try { await api.post(`/admin/power-studies/${o.id}/files`, fd); toast.success(`${label} uploaded`); reload(); }
    catch (err) { toast.error(errMsg(err, "Upload failed")); }
  };
  const has = o.files.some((f) => f.kind === kind);
  return (
    <label className={`cursor-pointer inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border ${has ? "border-[#84CC16]/40 text-[#84CC16]" : "border-white/15 text-slate-300"} hover:border-blue-500`}>
      <Upload size={13} /> {has && kind !== "other" ? `Replace ${label}` : `Upload ${label}`}
      <input data-testid={`upload-${kind}-${o.order_number}`} type="file" accept={ACCEPT} className="hidden" onChange={up} />
    </label>
  );
};

function OrderRow({ o, reload }) {
  const setStatus = async (status) => {
    try { await api.put(`/admin/power-studies/${o.id}/status`, { status }); toast.success(`Status: ${status}`); reload(); }
    catch (err) { toast.error(errMsg(err, "Could not update status")); }
  };
  const del = async (f) => { await api.delete(`/admin/power-studies/${o.id}/files/${f.id}`); reload(); };
  const dl = (f) => downloadBlob(api, `/power-study/files/${f.id}`, f.filename).catch(() => toast.error("Download failed"));
  const pending = !o.deposit_paid;
  return (
    <div className={`card p-5 ${pending ? "opacity-60" : ""}`} data-testid={`study-order-${o.order_number}`}>
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-[#84CC16]">{o.order_number} · {new Date(o.created_at).toLocaleString()}</p>
          <p className="text-white font-semibold mt-0.5">{o.site_address || `Parcel ${o.parcel_id}`}</p>
        </div>
        <div className="text-right text-xs space-y-1">
          <span data-testid={`study-status-${o.order_number}`} className="inline-block font-semibold px-3 py-1 rounded-full bg-[#2563EB]/20 text-blue-200 border border-blue-500/30">{o.status}</span>
          <p className="text-slate-400">Deposit {o.deposit_paid ? "paid" : "unpaid"} · Balance {o.balance_paid ? "paid" : "due"}</p>
          {o.balance_invoice_url && <a href={o.balance_invoice_url} target="_blank" rel="noopener noreferrer" className="text-[#3B82F6]">Stripe invoice</a>}
        </div>
      </div>
      <Info o={o} />
      {!pending && (
        <>
          <div className="flex flex-wrap gap-2 mt-4">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`set-status-${s.replace(/ /g, "-").toLowerCase()}-${o.order_number}`} disabled={o.status === s}
                onClick={() => setStatus(s)}
                className={`text-xs px-3 py-1.5 rounded-full border ${o.status === s ? "border-[#84CC16] text-[#84CC16]" : "border-white/15 text-slate-300 hover:border-blue-500"}`}>{s}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Marking "Ready" emails a $3,500 Stripe invoice. Requires all three report files.</p>
          <PrefilledTemplates o={o} />
          <div className="flex flex-wrap gap-2 mt-4">{KINDS.map(([k, l]) => <UploadBtn key={k} o={o} kind={k} label={l} reload={reload} />)}</div>
        </>
      )}
      {o.files.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {o.files.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-2 text-sm bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2">
              <span className="text-slate-300 truncate"><span className="text-slate-500">{f.label}:</span> {f.filename}</span>
              <span className="flex gap-3 shrink-0">
                <button onClick={() => dl(f)} className="text-[#3B82F6]" data-testid={`admin-download-${f.id}`}><Download size={15} /></button>
                {f.kind !== "brochure" && <button onClick={() => del(f)} className="text-red-400" data-testid={`admin-delete-file-${f.id}`}><Trash2 size={15} /></button>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PowerStudiesAdmin() {
  const [orders, setOrders] = useState(null);
  const [showPending, setShowPending] = useState(false);
  const load = useCallback(() => api.get("/admin/power-studies").then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!orders) return <p className="text-slate-400 font-mono">Loading…</p>;
  const list = orders.filter((o) => showPending || o.deposit_paid);
  return (
    <div data-testid="admin-power-studies">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-white">Power Feasibility Studies</h1>
          <p className="text-slate-400 text-sm mt-1">$5,000 studies · $1,500 deposit · $3,500 balance invoiced at "Ready". Files stay locked until paid in full.</p>
        </div>
        <label className="text-xs text-slate-400 flex items-center gap-2">
          <input type="checkbox" data-testid="show-pending-toggle" checked={showPending} onChange={(e) => setShowPending(e.target.checked)} /> Show unpaid checkouts
        </label>
      </div>
      <div className="space-y-4">
        {list.length === 0 && <p className="text-slate-500 text-sm" data-testid="no-study-orders">No study orders yet.</p>}
        {list.map((o) => <OrderRow key={o.id} o={o} reload={load} />)}
      </div>
    </div>
  );
}
